import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, X } from 'lucide-react';
import {
  Button,
  Checkbox,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { MEDICATION_CLASSIFICATIONS } from '../../constants/medications';
import AttachmentsCard from './AttachmentsCard';
import { getDosageUnit, getMedicationDisplayName } from './medicationUtils';
import MedicationSearch from './MedicationSearch';
import MedicationsCard from './MedicationsCard';
import {
  client,
  createPatientMedication,
  deletePatientMedication,
  getPatientMedications,
  updatePatientMedication,
} from '../../services/api';
import type {
  MedicationResponse,
  PatientMedicationCreate,
  PatientMedicationResponse,
  PatientMedicationUpdate,
} from '../../services/api';

interface PatientMedicationsProps {
  patientId: string;
}

const PatientMedications: React.FC<PatientMedicationsProps> = ({
  patientId,
}) => {
  // Medication list state
  const [medications, setMedications] = useState<PatientMedicationResponse[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMedication, setEditingMedication] =
    useState<PatientMedicationResponse | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Delete confirmation state
  const [deletingMedication, setDeletingMedication] =
    useState<PatientMedicationResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form state
  const [selectedMedication, setSelectedMedication] =
    useState<MedicationResponse | null>(null);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [amount, setAmount] = useState<string>('');
  const [discontinueDate, setDiscontinueDate] = useState<Date | undefined>(
    undefined
  );
  const [frequencyInstructions, setFrequencyInstructions] =
    useState<string>('');
  const [isLongstanding, setIsLongstanding] = useState(false);
  const [isChange, setIsChange] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [isNonstandardDosage, setIsNonstandardDosage] = useState(false);
  const [nonstandardDoses, setNonstandardDoses] = useState<string[]>(['']);
  const [isOffMarketMode, setIsOffMarketMode] = useState(false);
  const [offMarketDescription, setOffMarketDescription] = useState('');
  const [offMarketClassification, setOffMarketClassification] = useState('');

  // Edit mode state (medication change vs error correction)
  const [isMedicationChange, setIsMedicationChange] = useState(false);
  const [changeDate, setChangeDate] = useState<Date | undefined>(undefined);

  // Fetch medications
  const fetchMedications = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getPatientMedications({
        path: { patient_id: patientId },
        query: { include_discontinued: true },
        client,
      });
      if (response && response.data) {
        setMedications(response.data);
      } else {
        setMedications([]);
      }
    } catch (err) {
      console.error('Failed to fetch medications:', err);
      setError('Failed to load medications');
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchMedications();
  }, [fetchMedications]);

  const resetForm = () => {
    setSelectedMedication(null);
    setStartDate(undefined);
    setAmount('');
    setDiscontinueDate(undefined);
    setFrequencyInstructions('');
    setIsLongstanding(false);
    setIsChange(false);
    setIsNew(false);
    setIsNonstandardDosage(false);
    setNonstandardDoses(['']);
    setIsOffMarketMode(false);
    setOffMarketDescription('');
    setOffMarketClassification('');
    setEditingMedication(null);
    setIsMedicationChange(false);
    setChangeDate(undefined);
  };

  const handleAddOffMarket = (searchTerm: string) => {
    setIsOffMarketMode(true);
    setOffMarketDescription(searchTerm);
  };

  const openDialog = (
    mode: 'standard' | 'nonstandard' | 'offmarket' = 'standard'
  ) => {
    resetForm();
    if (mode === 'nonstandard') {
      setIsNonstandardDosage(true);
    } else if (mode === 'offmarket') {
      setIsOffMarketMode(true);
    }
    setIsDialogOpen(true);
  };

  const openEditDialog = (med: PatientMedicationResponse) => {
    setEditingMedication(med);
    setStartDate(new Date(med.start_date + 'T00:00:00'));
    setDiscontinueDate(
      med.discontinue_date
        ? new Date(med.discontinue_date + 'T00:00:00')
        : undefined
    );
    setAmount(med.amount != null ? String(med.amount) : '');
    setFrequencyInstructions(med.frequency_instructions ?? '');
    setIsLongstanding(med.is_longstanding ?? false);
    setIsChange(med.is_change ?? false);
    setIsNew(med.is_new ?? false);
    setIsNonstandardDosage(med.is_nonstandard_dosage ?? false);
    setNonstandardDoses(
      med.nonstandard_doses && med.nonstandard_doses.length > 0
        ? med.nonstandard_doses
        : ['']
    );
    setIsOffMarketMode(med.is_off_market ?? false);
    setOffMarketDescription(med.off_market_description ?? '');
    setOffMarketClassification(med.off_market_classification ?? '');
    setIsMedicationChange(true); // Default to Medication Change mode
    setChangeDate(undefined);

    if (med.medication) {
      setSelectedMedication(med.medication);
    }

    setIsDialogOpen(true);
  };

  const addDoseRow = () => {
    setNonstandardDoses([...nonstandardDoses, '']);
  };

  const removeDoseRow = (index: number) => {
    setNonstandardDoses(nonstandardDoses.filter((_, i) => i !== index));
  };

  const updateDoseRow = (index: number, value: string) => {
    const updated = [...nonstandardDoses];
    updated[index] = value;
    setNonstandardDoses(updated);
  };

  const handleDialogClose = (open: boolean) => {
    if (!open && !isSaving) {
      setIsDialogOpen(false);
      resetForm();
    }
  };

  const formatDateForApi = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  const handleSaveMedication = async () => {
    if (!startDate) return;
    if (editingMedication && isMedicationChange && !changeDate) return;

    setIsSaving(true);
    try {
      if (editingMedication) {
        if (isMedicationChange && changeDate) {
          // Medication change: discontinue old med and create new one
          // 1. Update the existing medication with discontinue date = change date
          const updateResponse = await updatePatientMedication({
            path: {
              patient_id: patientId,
              medication_id: editingMedication.id,
            },
            body: {
              discontinue_date: formatDateForApi(changeDate),
            },
            client,
          });

          // 2. Create a new medication with start date = change date
          const createData: PatientMedicationCreate = {
            start_date: formatDateForApi(changeDate),
            discontinue_date: discontinueDate
              ? formatDateForApi(discontinueDate)
              : null,
            amount: isNonstandardDosage
              ? null
              : amount
                ? parseFloat(amount)
                : null,
            frequency_instructions: frequencyInstructions || null,
            is_nonstandard_dosage: isNonstandardDosage,
            nonstandard_doses: isNonstandardDosage
              ? nonstandardDoses.filter(d => d.trim())
              : null,
            is_longstanding: isLongstanding,
            is_change: true, // Mark as changed since this is a medication change
            is_new: isNew,
            is_off_market: isOffMarketMode,
            off_market_description: isOffMarketMode
              ? offMarketDescription
              : null,
            off_market_classification: isOffMarketMode
              ? offMarketClassification || null
              : null,
            medication_id:
              !isOffMarketMode && selectedMedication
                ? selectedMedication.id
                : null,
          };

          const createResponse = await createPatientMedication({
            path: { patient_id: patientId },
            body: createData,
            client,
          });

          // Eagerly update state
          if (updateResponse?.data && createResponse?.data) {
            setMedications(prev =>
              prev
                .map(m =>
                  m.id === editingMedication.id ? updateResponse.data! : m
                )
                .concat(createResponse.data!)
            );
          }
        } else {
          // Error correction: just update the existing medication
          const updateData: PatientMedicationUpdate = {
            start_date: formatDateForApi(startDate),
            discontinue_date: discontinueDate
              ? formatDateForApi(discontinueDate)
              : null,
            amount: isNonstandardDosage
              ? null
              : amount
                ? parseFloat(amount)
                : null,
            frequency_instructions: frequencyInstructions || null,
            is_nonstandard_dosage: isNonstandardDosage,
            nonstandard_doses: isNonstandardDosage
              ? nonstandardDoses.filter(d => d.trim())
              : null,
            is_longstanding: isLongstanding,
            is_change: isChange,
            is_new: isNew,
            is_off_market: isOffMarketMode,
            off_market_description: isOffMarketMode
              ? offMarketDescription
              : null,
            off_market_classification: isOffMarketMode
              ? offMarketClassification || null
              : null,
          };

          if (!isOffMarketMode && selectedMedication) {
            updateData.medication_id = selectedMedication.id;
          }

          const response = await updatePatientMedication({
            path: {
              patient_id: patientId,
              medication_id: editingMedication.id,
            },
            body: updateData,
            client,
          });

          // Eagerly update state
          if (response?.data) {
            setMedications(prev =>
              prev.map(m =>
                m.id === editingMedication.id ? response.data! : m
              )
            );
          }
        }
      } else {
        // Create new medication
        const createData: PatientMedicationCreate = {
          start_date: formatDateForApi(startDate),
          discontinue_date: discontinueDate
            ? formatDateForApi(discontinueDate)
            : null,
          amount: isNonstandardDosage
            ? null
            : amount
              ? parseFloat(amount)
              : null,
          frequency_instructions: frequencyInstructions || null,
          is_nonstandard_dosage: isNonstandardDosage,
          nonstandard_doses: isNonstandardDosage
            ? nonstandardDoses.filter(d => d.trim())
            : null,
          is_longstanding: isLongstanding,
          is_change: isChange,
          is_new: isNew,
          is_off_market: isOffMarketMode,
          off_market_description: isOffMarketMode ? offMarketDescription : null,
          off_market_classification: isOffMarketMode
            ? offMarketClassification || null
            : null,
          medication_id:
            !isOffMarketMode && selectedMedication
              ? selectedMedication.id
              : null,
        };

        const response = await createPatientMedication({
          path: { patient_id: patientId },
          body: createData,
          client,
        });

        // Eagerly update state
        if (response?.data) {
          setMedications(prev => [...prev, response.data!]);
        }
      }

      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      console.error('Failed to save medication:', err);
      setError('Failed to save medication');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteMedication = async () => {
    if (!deletingMedication) return;

    setIsDeleting(true);
    try {
      await deletePatientMedication({
        path: {
          patient_id: patientId,
          medication_id: deletingMedication.id,
        },
        client,
      });

      // Eagerly update state
      setMedications(prev => prev.filter(m => m.id !== deletingMedication.id));
      setDeletingMedication(null);
    } catch (err) {
      console.error('Failed to delete medication:', err);
      setError('Failed to delete medication');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleQuickDiscontinue = async (medicationId: string, date: Date) => {
    const response = await updatePatientMedication({
      path: {
        patient_id: patientId,
        medication_id: medicationId,
      },
      body: {
        discontinue_date: formatDateForApi(date),
      },
      client,
    });

    if (response?.data) {
      setMedications(prev =>
        prev.map(m => (m.id === medicationId ? response.data! : m))
      );
    }
  };

  // Checkbox disable logic
  const isChangeOrNewSelected = isChange || isNew;
  const isLongstandingSelected = isLongstanding;

  const handleMedicationSelect = (medication: MedicationResponse) => {
    setSelectedMedication(medication);
  };

  const dosageUnit = getDosageUnit(selectedMedication);

  return (
    <>
      <MedicationsCard
        medications={medications}
        isLoading={isLoading}
        error={error}
        onRetry={fetchMedications}
        onAddMedication={openDialog}
        onEditMedication={openEditDialog}
        onDeleteMedication={setDeletingMedication}
        onDiscontinueMedication={handleQuickDiscontinue}
      />

      <AttachmentsCard patientId={patientId} />

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className='max-w-2xl'>
          <DialogHeader>
            <DialogTitle>
              {editingMedication
                ? 'Edit Medication'
                : isOffMarketMode
                  ? 'Add Off Market / Unlisted Medication'
                  : 'Add Medication'}
            </DialogTitle>
            <DialogDescription>
              {editingMedication
                ? 'Choose whether to correct an error or make a medication change.'
                : isOffMarketMode
                  ? 'Enter the details for the unlisted medication.'
                  : 'Search for a medication and enter the dosage details.'}
            </DialogDescription>
            {isOffMarketMode &&
              (!editingMedication || !editingMedication.is_off_market) && (
                <div className='mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800'>
                  <p>
                    Use only for medications not found in the Medispan database.
                  </p>
                  <p>
                    These medications will not be included in the clinical
                    interaction checks.
                  </p>
                </div>
              )}
          </DialogHeader>

          <div className='space-y-4 py-4'>
            {/* Edit Mode Switch - only shown when editing */}
            {editingMedication && (
              <div className='p-3 bg-muted/50 rounded-lg space-y-3'>
                <div className='flex items-center gap-3'>
                  <span
                    className={`text-sm ${!isMedicationChange ? 'font-medium' : 'text-muted-foreground'}`}
                  >
                    Error Correction
                  </span>
                  <Switch
                    checked={isMedicationChange}
                    onCheckedChange={setIsMedicationChange}
                  />
                  <span
                    className={`text-sm ${isMedicationChange ? 'font-medium' : 'text-muted-foreground'}`}
                  >
                    Medication Change
                  </span>
                </div>
                <p className='text-sm text-muted-foreground'>
                  {isMedicationChange
                    ? 'Existing medication will be discontinued and new medication created with changed information.'
                    : 'Existing medication will be updated.'}
                </p>
                {isMedicationChange && (
                  <div>
                    <Label className='text-xs text-muted-foreground mb-1 block'>
                      Change Date
                    </Label>
                    <DatePicker
                      date={changeDate}
                      onDateChange={setChangeDate}
                      placeholder='Select change date'
                      datePickerSize='default'
                      minDate={
                        new Date(editingMedication.start_date + 'T00:00:00')
                      }
                    />
                  </div>
                )}
              </div>
            )}

            {/* Mode Switches */}
            <div className='flex items-center gap-4'>
              <div className='flex items-center gap-1.5'>
                <span
                  className={`text-xs ${isOffMarketMode ? 'font-medium' : 'text-muted-foreground'}`}
                >
                  {isOffMarketMode ? 'Off Market Medication' : 'Off Market'}
                </span>
                <Switch
                  checked={!isOffMarketMode}
                  onCheckedChange={checked => {
                    const goingOffMarket = !checked;
                    setIsOffMarketMode(goingOffMarket);
                    if (goingOffMarket) {
                      setIsNonstandardDosage(true);
                      // Copy medication name to off-market description when switching
                      if (
                        selectedMedication?.full_name &&
                        !offMarketDescription
                      ) {
                        setOffMarketDescription(selectedMedication.full_name);
                      }
                    }
                  }}
                  className='scale-75'
                />
                <span
                  className={`text-xs ${!isOffMarketMode ? 'font-medium' : 'text-muted-foreground'}`}
                >
                  {isOffMarketMode ? 'On Market' : 'On Market Medication'}
                </span>
              </div>
              <div className='flex items-center gap-1.5'>
                <span
                  className={`text-xs ${isNonstandardDosage ? 'font-medium' : 'text-muted-foreground'} ${isOffMarketMode ? 'opacity-50' : ''}`}
                >
                  {isNonstandardDosage ? 'Nonstandard Dosage' : 'Nonstandard'}
                </span>
                <Switch
                  checked={!isNonstandardDosage}
                  onCheckedChange={checked => setIsNonstandardDosage(!checked)}
                  disabled={isOffMarketMode}
                  className='scale-75'
                />
                <span
                  className={`text-xs ${!isNonstandardDosage ? 'font-medium' : 'text-muted-foreground'} ${isOffMarketMode ? 'opacity-50' : ''}`}
                >
                  {isNonstandardDosage ? 'Standard' : 'Standard Dosage'}
                </span>
              </div>
            </div>

            {/* Row 1: Start Date | Discontinue Date */}
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <Label className='text-xs text-muted-foreground mb-1 block'>
                  Start Date
                </Label>
                <DatePicker
                  date={startDate}
                  onDateChange={date => {
                    setStartDate(date);
                    if (date && discontinueDate && date > discontinueDate) {
                      setDiscontinueDate(undefined);
                    }
                  }}
                  placeholder='Select date'
                  datePickerSize='default'
                />
              </div>
              <div>
                <Label className='text-xs text-muted-foreground mb-1 block'>
                  Discontinue Date
                </Label>
                <div className='flex items-center gap-1'>
                  <DatePicker
                    date={discontinueDate}
                    onDateChange={setDiscontinueDate}
                    placeholder='Optional'
                    datePickerSize='default'
                    minDate={startDate}
                  />
                  {discontinueDate && (
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='h-9 w-9 p-0 text-muted-foreground hover:text-foreground'
                      onClick={() => setDiscontinueDate(undefined)}
                    >
                      <X className='h-4 w-4' />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {isOffMarketMode ? (
              /* Off-market medication form */
              <>
                {/* Medication Description */}
                <div>
                  <Label className='text-xs text-muted-foreground mb-1 block'>
                    Drug / Route / Strength / Amount / Form / Frequency /
                    Comments
                  </Label>
                  <Textarea
                    value={offMarketDescription}
                    onChange={e => setOffMarketDescription(e.target.value)}
                    placeholder='e.g., Aspirin 325mg tablet, take 1 tablet by mouth daily with food'
                    textareaSize='default'
                    rows={3}
                  />
                </div>

                {/* Classification */}
                <div>
                  <Label className='text-xs text-muted-foreground mb-1 block'>
                    Classification
                  </Label>
                  <Select
                    value={offMarketClassification}
                    onValueChange={setOffMarketClassification}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder='Select classification' />
                    </SelectTrigger>
                    <SelectContent>
                      {MEDICATION_CLASSIFICATIONS.map(classification => (
                        <SelectItem
                          key={classification.value}
                          value={classification.value}
                        >
                          {classification.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              /* Standard medication form */
              <>
                {/* Medication | Amount (or just Medication if nonstandard) */}
                <div className='grid grid-cols-12 gap-3 items-end'>
                  <div
                    className={
                      isNonstandardDosage ? 'col-span-12' : 'col-span-8'
                    }
                  >
                    <Label className='text-xs text-muted-foreground mb-1 block'>
                      Medication
                    </Label>
                    <MedicationSearch
                      value={selectedMedication}
                      onSelect={handleMedicationSelect}
                      onClear={() => setSelectedMedication(null)}
                      onAddOffMarket={handleAddOffMarket}
                      placeholder='Search medications...'
                    />
                  </div>
                  {!isNonstandardDosage && (
                    <div className='col-span-4'>
                      <Label className='text-xs text-muted-foreground mb-1 block'>
                        Amount
                      </Label>
                      <div className='flex items-center gap-2'>
                        <Input
                          type='number'
                          value={amount}
                          onChange={e => setAmount(e.target.value)}
                          placeholder='0'
                          inputSize='default'
                          className='w-20'
                        />
                        {dosageUnit && (
                          <span className='text-sm text-muted-foreground whitespace-nowrap'>
                            {dosageUnit}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Nonstandard Dose Rows */}
                {isNonstandardDosage && (
                  <div className='space-y-2'>
                    <Label className='text-xs text-muted-foreground block'>
                      Dose
                    </Label>
                    {nonstandardDoses.map((dose, index) => (
                      <div key={index} className='flex items-center gap-2'>
                        <Input
                          type='text'
                          value={dose}
                          onChange={e => updateDoseRow(index, e.target.value)}
                          placeholder='Enter dose (e.g., 1 tablet in the morning)'
                          inputSize='default'
                          className='flex-1'
                        />
                        {index === nonstandardDoses.length - 1 && (
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            onClick={addDoseRow}
                          >
                            <Plus className='h-4 w-4' />
                          </Button>
                        )}
                        {index > 0 && (
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => removeDoseRow(index)}
                          >
                            <Trash2 className='h-4 w-4 text-muted-foreground' />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Frequency / Instructions */}
                <div>
                  <Label className='text-xs text-muted-foreground mb-1 block'>
                    Frequency / Instructions
                  </Label>
                  <Textarea
                    value={frequencyInstructions}
                    onChange={e => setFrequencyInstructions(e.target.value)}
                    placeholder='e.g., Take 1 tablet by mouth twice daily with food'
                    textareaSize='default'
                    rows={2}
                  />
                </div>
              </>
            )}

            {/* Status checkboxes - shown in all modes */}
            <TooltipProvider>
              <div className='flex items-center gap-6'>
                <div className='flex items-center gap-2'>
                  <Checkbox
                    id='longstanding'
                    checked={isLongstanding}
                    onCheckedChange={checked =>
                      setIsLongstanding(checked === true)
                    }
                    disabled={isChangeOrNewSelected}
                  />
                  <Label
                    htmlFor='longstanding'
                    className={`text-sm cursor-pointer ${isChangeOrNewSelected ? 'text-muted-foreground' : ''}`}
                  >
                    Longstanding
                  </Label>
                </div>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className='flex items-center gap-2 cursor-default'>
                      <Checkbox
                        id='change'
                        checked={isChange}
                        onCheckedChange={checked =>
                          setIsChange(checked === true)
                        }
                        disabled={isLongstandingSelected}
                      />
                      <Label
                        htmlFor='change'
                        className={`text-sm cursor-pointer ${isLongstandingSelected ? 'text-muted-foreground' : ''}`}
                      >
                        Change
                      </Label>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Medication changed within last 60 days</p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className='flex items-center gap-2 cursor-default'>
                      <Checkbox
                        id='new'
                        checked={isNew}
                        onCheckedChange={checked => setIsNew(checked === true)}
                        disabled={isLongstandingSelected}
                      />
                      <Label
                        htmlFor='new'
                        className={`text-sm cursor-pointer ${isLongstandingSelected ? 'text-muted-foreground' : ''}`}
                      >
                        New
                      </Label>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Medication added within last 30 days</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => handleDialogClose(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveMedication}
              disabled={
                isSaving ||
                (editingMedication && isMedicationChange && !changeDate) ||
                (isOffMarketMode
                  ? !offMarketDescription || !startDate
                  : !selectedMedication ||
                    !startDate ||
                    !frequencyInstructions.trim() ||
                    (isNonstandardDosage
                      ? !nonstandardDoses.some(d => d.trim())
                      : !amount || parseFloat(amount) <= 0))
              }
            >
              {isSaving && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
              {editingMedication
                ? isMedicationChange
                  ? 'Save as New Medication'
                  : 'Save Changes'
                : 'Add Medication'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deletingMedication}
        onOpenChange={(open: boolean) => !open && setDeletingMedication(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Medication</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <strong>
                {deletingMedication &&
                  getMedicationDisplayName(deletingMedication)}
              </strong>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setDeletingMedication(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleDeleteMedication}
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PatientMedications;
